"use client";

import { Dialog, Transition } from "@headlessui/react";
import { EnvelopeIcon, UserIcon } from "@heroicons/react/24/outline";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ElementRef, Fragment, useEffect, useRef } from "react";

import { closeLoginDialog } from "@/context/redux/reducers/dialog/login-dialog-slice";
import { useAppDispatch, useAppSelector } from "@/hooks/redux-hooks";
import { useAuthUrls } from "@/hooks/use-auth-urls";
import { useLang } from "@/hooks/use-lang";

import { Button } from "ui/core/button";

import { GoogleLoginButton } from "../../google-login-button";

export const LoginDialog = () => {
  const pathname = usePathname();
  const { isOpen } = useAppSelector((state) => state.loginDialog);
  const { t } = useLang();
  const dispatch = useAppDispatch();
  const signupLinkRef = useRef<ElementRef<typeof Link>>(null);
  const { loginUrl, signupUrl } = useAuthUrls();

  useEffect(() => {
    return () => {
      dispatch(closeLoginDialog());
    };
  }, [dispatch, pathname]);

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        id="login-dialog"
        as="div"
        className="relative z-[99998]"
        onClose={() => null}
        initialFocus={signupLinkRef}
      >
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm"
          aria-hidden="true"
        />
        <div className="fixed inset-0 flex items-end justify-center p-4 sm:items-center">
          <Dialog.Panel className="relative w-full rounded-lg bg-white p-4 pt-5 shadow-xl sm:max-w-lg sm:p-6">
            <div>
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-sky-100">
                <UserIcon className="h-6 w-6 text-sky-600" aria-hidden="true" />
              </div>
              <div className="mt-3 text-center sm:mt-5">
                <Dialog.Title
                  as="h3"
                  className="text-base font-semibold leading-6 text-gray-900"
                >
                  {t.blockDialog.login.title}
                </Dialog.Title>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    {t.blockDialog.login.description[0]}
                    <Link
                      href={loginUrl}
                      className="text-sky-700"
                      onClick={() => {
                        dispatch(closeLoginDialog());
                      }}
                    >
                      {t.blockDialog.login.login}
                    </Link>
                    {t.blockDialog.login.description[1]}
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-3 sm:mt-6 sm:grid-cols-2">
              <Link
                ref={signupLinkRef}
                href={signupUrl}
                onClick={() => {
                  dispatch(closeLoginDialog());
                }}
              >
                <Button className="w-full py-2">
                  <span className="flex items-center justify-center gap-1.5">
                    <EnvelopeIcon className="h-5 w-5" />
                    {t.blockDialog.login.emailSignup}
                  </span>
                </Button>
              </Link>
              <GoogleLoginButton className="h-9 w-full" callbackUrl={pathname}>
                {t.blockDialog.login.googleSignup}
              </GoogleLoginButton>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
